import {
  AppBar,
  Box,
  IconButton,
  styled,
  SxProps,
  Theme,
  Toolbar,
  Typography,
  TypographyProps,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import React from 'react';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { BackButton } from '../BackButton/BackButton';
import { CloseButton } from '../CloseButton/CloseButton';

const StyledTypography = styled(Typography)(({ theme }) => ({
  textWrap: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  padding: `0 ${theme.spacing(1)}`,
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.grey[800],
}));

interface ModalHeaderProps {
  modalTitle: string;
  onClickClose: () => void;
  onClickBack: () => void;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  modalTitleProps?: TypographyProps;
  headerStyles?: SxProps<Theme>;
  /** Show the favorite star in the left slot. Optional. */
  showFavorite?: boolean;
  /** Current favorite state. */
  isFavorited?: boolean;
  /** Called with the next favorite state when the star is tapped. */
  onToggleFavorite?: (nextIsFavorited: boolean) => void;
  /** Show the expand button beside the close button. Optional. */
  showExpandButton?: boolean;
  /** Called when the expand button is clicked. */
  onClickExpand?: () => void;
}

export const ModalHeader = ({
  modalTitle,
  onClickClose,
  onClickBack,
  showBackButton,
  showCloseButton,
  modalTitleProps,
  headerStyles,
  showFavorite = false,
  isFavorited = false,
  onToggleFavorite,
  showExpandButton = false,
  onClickExpand,
}: ModalHeaderProps) => {
  const { translation } = useTranslationContext();

  const handleToggleFavorite = () => {
    const next = !isFavorited;
    onToggleFavorite?.(next);
  };

  const FavoriteStar = isFavorited ? StarIcon : StarBorderIcon;

  return (
    <StyledAppBar position="sticky" className="modal-header" sx={headerStyles}>
      <Toolbar
        disableGutters
        sx={{ justifyContent: 'space-between', px: 1.75 }}
        className="modal-header__toolbar"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {showBackButton && (
            <BackButton
              className="modal-header__back-button"
              onClick={onClickBack}
              color="inherit"
            />
          )}
          {showFavorite && (
            <IconButton
              className="modal-header__favorite-button"
              type="button"
              onClick={handleToggleFavorite}
              color="inherit"
              aria-pressed={isFavorited}
              aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
              sx={{
                // Responsive margin: tighter on small screens, default on larger.
                ml: showBackButton ? { xs: -1, sm: 0, md: 0, lg: 0.5, xl: 1 } : 0,
              }}
            >
              <FavoriteStar
                aria-hidden
                focusable={false}
                sx={{ fontSize: '28px', color: (theme) => theme.palette.secondary.main }}
              />
            </IconButton>
          )}
        </Box>

        <StyledTypography
          className="modal-header__title"
          aria-label={modalTitle}
          variant="h6"
          fontWeight="normal"
          color="inherit"
          {...modalTitleProps}
          sx={{
            flex: 1,
            minWidth: 0, // lets ellipsis work inside the flex row
            textAlign: 'center',
            ...modalTitleProps?.sx,
          }}
        >
          {modalTitle}
        </StyledTypography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {showExpandButton && (
            <IconButton
              className="modal-header__expand-button"
              onClick={onClickExpand}
              color="inherit"
              aria-label={translation('enter_full_screen_accessibility')}
            >
              <OpenInFullIcon aria-hidden focusable={false} fontSize="small" />
            </IconButton>
          )}
          <Box visibility={showCloseButton ? 'visible' : 'hidden'}>
            <CloseButton
              className="modal-header__close-button"
              onClick={onClickClose}
              color="inherit"
            />
          </Box>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};
